import {memoize} from "lodash";
import root from "window-or-global";

// temporary circular dependency @atg-shared/log --> @atg-shared/feature-experimentation --> @atg-shared/log. Will be resolved when removing the feature toggle for new logger
// eslint-disable-next-line @nx/enforce-module-boundaries
import log from "@atg-shared/log";
import {noTrans} from "@atg-shared/no-trans";
import {currentTrackingSystem} from "@atg-shared/system";
import {Flags} from "./constants";

type FlagDefinition = (typeof Flags)[keyof typeof Flags];

type VariableTypeMap = {
    string: string;
    boolean: boolean;
    number: number;
    json: Record<string, unknown>;
};

type UnknownVariables = Record<string, VariableTypeMap[keyof VariableTypeMap]>;

type VariableValues<TVariables extends Record<string, {type: keyof VariableTypeMap}>> = {
    [Name in keyof TVariables]: VariableTypeMap[TVariables[Name]["type"]];
} & UnknownVariables;

export type DecisionResult<T extends FlagDefinition> = {
    variables: VariableValues<T["variables"]>;
    reasons: string[];
    variationKey: keyof T["variations"] | (T["hasFallback"] extends true ? never : null);
};

function resolveVariables<T extends FlagDefinition>(
    flag: T,
    rawVariables?: UnknownVariables,
): DecisionResult<T>["variables"] {
    const flagVariables = flag.variables ?? {};

    const variablesWithDefinition: UnknownVariables = {};

    for (const [variableName, variableDefinition] of Object.entries(flagVariables)) {
        const {defaultValue} = variableDefinition;
        const rawValue = rawVariables?.[variableName];

        variablesWithDefinition[variableName] =
            rawValue === undefined ? defaultValue : rawValue;
    }

    // merge rawVariables with variablesWithDefinition to ensure that any variables not defined in the flag's variables are still included in the result
    return {
        ...rawVariables,
        ...variablesWithDefinition,
    } as DecisionResult<T>["variables"];
}

/**
 * Makes a feature flag decision using the Optimizely user context.
 *
 * @param flag - The flag constant (e.g., Flags.PLAY_HERO_V2)
 * @returns The decision result from Optimizely, including the variation key and fallback handling
 *
 * @example
 * ```typescript
 * const decision = decide(Flags.PLAY_HERO_V2);
 * if (decision.variationKey === "on") {
 *   // Feature is enabled
 * }
 * ```
 */
export const decide = memoize(<T extends FlagDefinition>(flag: T): DecisionResult<T> => {
    const flagKey = flag.key;

    const userContext = root.optimizelyUserContext;
    const fallbackValue = flag.fallbackVariation;

    if (!userContext) {
        const hasOptimizelyDataFile = !!root.optimizelyDataFile;
        const system = currentTrackingSystem();

        // we know PAW_NEW_CLIENT_LOGGER is being decided before Optimizely is loaded on app start, do not log it.
        if (flagKey !== Flags.PAW_NEW_CLIENT_LOGGER.key) {
            // we want to know if we did not start/finish loading file, or if Optimizely was down
            log.error("Optimizely user context is not available", {
                flagKey,
                system,
                hasOptimizelyDataFile,
                optimizelyConfigStatus: root.optimizelyConfigStatus,
            });
        }

        return {
            variables: resolveVariables(flag),
            reasons: [noTrans("Optimizely user context is not available")],
            variationKey: fallbackValue ?? null,
        } as DecisionResult<T>;
    }

    const result = userContext.decide(flagKey);

    return {
        variables: resolveVariables(flag, result.variables as UnknownVariables),
        reasons: result.reasons,
        variationKey: result.variationKey ?? fallbackValue ?? null,
    } as DecisionResult<T>;
});
